import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MultiSelect } from '../components/FilterBar';

const API = process.env.REACT_APP_API_URL || '';

// ── URL helpers ─────────────────────────────────────────
const PFX = 'ssm_';
function getP(key) {
  return new URLSearchParams(window.location.search).get(PFX + key) || '';
}
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = Array.isArray(val) ? val.join('|') : (val || '');
  if (v && v !== 'ALL') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearAllParams() {
  const sp = new URLSearchParams(window.location.search);
  ['s','tp','pg','grp','mk','gdn'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

// ── Sort hook ───────────────────────────────────────────
function useSortable(data, defaultCol, defaultDir = 'desc') {
  const [sort, setSort] = useState({ col: defaultCol, dir: defaultDir });
  const toggle = col =>
    setSort(s => s.col === col
      ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' });
  const sorted = useMemo(() => {
    if (!data.length) return data;
    return [...data].sort((a, b) => {
      const av = a[sort.col], bv = b[sort.col];
      const cmp = typeof av === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [data, sort.col, sort.dir]);
  return [sorted, sort, toggle];
}

// ── Sub-components ──────────────────────────────────────
function Th({ col, label, sort, toggle, right }) {
  const active = sort.col === col;
  return (
    <th className={active ? 'sorted' : ''}
      style={right ? { textAlign: 'right' } : {}}
      onClick={() => toggle(col)}>
      {label}
      <span className="sort-arrow">{active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
    </th>
  );
}

function MismatchBadge({ type }) {
  return type === 'Extra serials'
    ? <span className="badge" style={{ color:'#e53e3e', borderColor:'#fed7d7', background:'#fff5f5' }}>Extra serials</span>
    : <span className="badge" style={{ color:'#b7791f', borderColor:'#fefcbf', background:'#fffff0' }}>Missing serials</span>;
}

function SerialCell({ list }) {
  const [expanded, setExpanded] = useState(false);
  if (!list) return <span style={{ color:'var(--text4,#aaa)', fontSize:11 }}>—</span>;
  const lines = list.split('\n').filter(Boolean);
  if (!lines.length) return <span style={{ color:'var(--text4,#aaa)', fontSize:11 }}>—</span>;
  const LIMIT   = 3;
  const visible = expanded ? lines : lines.slice(0, LIMIT);
  return (
    <div style={{ fontFamily:'var(--mono,monospace)', fontSize:11, lineHeight:1.65 }}>
      {visible.map((s, i) => <div key={i}>{s}</div>)}
      {lines.length > LIMIT && (
        <button onClick={() => setExpanded(e => !e)}
          style={{ marginTop:4, padding:'2px 8px', fontSize:10,
            fontFamily:'var(--mono,monospace)',
            background:'var(--surface2,#f5f5f5)', border:'1px solid var(--border,#e2e8f0)',
            borderRadius:'var(--r,6px)', color:'var(--blue,#3182ce)', cursor:'pointer' }}>
          {expanded ? '▲ less' : `+${lines.length - LIMIT} more`}
        </button>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────
export default function SerialStockMismatch({ db }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // State initialised from URL
  const [search,       setSearchRaw]       = useState(() => getP('s'));
  const [filterType,   setFilterTypeRaw]   = useState(() => { const v = getP('tp');  return v ? v.split('|') : []; });
  const [filterParent, setFilterParentRaw] = useState(() => { const v = getP('pg');  return v ? v.split('|') : []; });
  const [filterGroup,  setFilterGroupRaw]  = useState(() => { const v = getP('grp'); return v ? v.split('|') : []; });
  const [filterMake,   setFilterMakeRaw]   = useState(() => { const v = getP('mk');  return v ? v.split('|') : []; });
  const [filterGodown, setFilterGodownRaw] = useState(() => { const v = getP('gdn'); return v ? v.split('|') : []; });

  // Setters that update state + URL together
  const setSearch       = v => { setSearchRaw(v);       setP('s',   v); };
  const setFilterType   = v => { setFilterTypeRaw(v);   setP('tp',  v); };
  const setFilterParent = v => { setFilterParentRaw(v); setP('pg',  v); };
  const setFilterGroup  = v => { setFilterGroupRaw(v);  setP('grp', v); };
  const setFilterMake   = v => { setFilterMakeRaw(v);   setP('mk',  v); };
  const setFilterGodown = v => { setFilterGodownRaw(v); setP('gdn', v); };

  const token = localStorage.getItem('bc_token') || '';

  const load = useCallback(() => {
    if (!db) return;
    setLoading(true); setError('');
    fetch(`${API}/api/serial-stock-mismatch?db=${encodeURIComponent(db)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(j => { if (j.error) throw new Error(j.error); setRows(j.data || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [db, token]);

  useEffect(() => { load(); }, [load]);

  // Filter options
  const typeOpts   = useMemo(() => [...new Set(rows.map(r => r.MismatchType))].filter(Boolean).sort(),    [rows]);
  const parentOpts = useMemo(() => [...new Set(rows.map(r => r.ParentItemGroup))].filter(Boolean).sort(), [rows]);
  const groupOpts  = useMemo(() => [...new Set(rows.map(r => r.ItemGroup))].filter(Boolean).sort(),       [rows]);
  const makeOpts   = useMemo(() => [...new Set(rows.map(r => r.Make))].filter(Boolean).sort(),            [rows]);
  const godownOpts = useMemo(() => [...new Set(rows.map(r => r.GodownName))].filter(Boolean).sort(),      [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      if (filterType.length   && !filterType.includes(r.MismatchType))      return false;
      if (filterParent.length && !filterParent.includes(r.ParentItemGroup)) return false;
      if (filterGroup.length  && !filterGroup.includes(r.ItemGroup))        return false;
      if (filterMake.length   && !filterMake.includes(r.Make))              return false;
      if (filterGodown.length && !filterGodown.includes(r.GodownName))      return false;
      if (q && !r.ItemName?.toLowerCase().includes(q)
            && !r.Make?.toLowerCase().includes(q)
            && !r.ItemGroup?.toLowerCase().includes(q)
            && !r.SerialList?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, filterType, filterParent, filterGroup, filterMake, filterGodown]);

  const [sorted, sort, toggleSort] = useSortable(filtered, 'Difference', 'desc');

  const hasFilters = !!(search || filterType.length || filterParent.length
                     || filterGroup.length || filterMake.length || filterGodown.length);

  function clearFilters() {
    setSearchRaw(''); setFilterTypeRaw([]); setFilterParentRaw([]);
    setFilterGroupRaw([]); setFilterMakeRaw([]); setFilterGodownRaw([]);
    clearAllParams();
  }

  const totalExtra   = filtered.filter(r => r.MismatchType === 'Extra serials').length;
  const totalMissing = filtered.filter(r => r.MismatchType === 'Missing serials').length;
  const maxDiff      = filtered.reduce((m, r) => Math.max(m, Math.abs(r.Difference || 0)), 0);

  return (
    <div>
      <div className="page-head">
        <div className="page-title">Serial Stock Mismatch</div>
        <div className="page-sub">B22 items where stock qty ≠ in-stock serial count · {db}</div>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="sum-card">
          <div className="sum-card-label">Total Mismatches</div>
          <div className="sum-card-value accent">{filtered.length}</div>
          <div className="sum-card-sub">
            {hasFilters && filtered.length !== rows.length ? `of ${rows.length} total` : 'items · all godowns'}
          </div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Extra Serials</div>
          <div className="sum-card-value red">{totalExtra}</div>
          <div className="sum-card-sub">serial &gt; stock qty</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Missing Serials</div>
          <div className="sum-card-value orange">{totalMissing}</div>
          <div className="sum-card-sub">stock qty &gt; serial</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Max Difference</div>
          <div className="sum-card-value">{maxDiff}</div>
          <div className="sum-card-sub">units in single row</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-row-a">
          <div className="search-box">
            <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input placeholder="Search item, make, serial…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className={`btn-refresh${loading ? ' spinning' : ''}`} onClick={load} disabled={loading}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span>Refresh</span>
          </button>
          <span className="count-badge">{filtered.length} / {rows.length}</span>
        </div>
        <div className="toolbar-row-b">
          <MultiSelect label="Type"         options={typeOpts}   selected={filterType}   onChange={setFilterType}   />
          <MultiSelect label="Parent Group" options={parentOpts} selected={filterParent} onChange={setFilterParent} />
          <MultiSelect label="Group"        options={groupOpts}  selected={filterGroup}  onChange={setFilterGroup}  />
          <MultiSelect label="Make"         options={makeOpts}   selected={filterMake}   onChange={setFilterMake}   />
          <MultiSelect label="Godown"       options={godownOpts} selected={filterGodown} onChange={setFilterGodown} />
          <button className="btn-reset" onClick={clearFilters} disabled={!hasFilters}>↺ Reset</button>
        </div>
      </div>

      {error && (
        <div className="error-bar">
          <span className="error-icon">⚠</span>
          <div><div>Failed to load data</div><div className="error-detail">{error}</div></div>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner" /><span>Loading serial stock mismatch…</span></div>
      ) : !rows.length && !error ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>No mismatches found</h3>
          <p>All B22 items have matching stock and serial counts.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>No results</h3>
          <p>Try adjusting your filters or search term.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <Th col="ItemName"     label="Item Name"    sort={sort} toggle={toggleSort} />
                <Th col="GodownName"   label="Godown"       sort={sort} toggle={toggleSort} />
                <Th col="MismatchType" label="Type"         sort={sort} toggle={toggleSort} />
                <Th col="StockQty"     label="Stock Qty"    sort={sort} toggle={toggleSort} right />
                <Th col="SerialQty"    label="Serial Count" sort={sort} toggle={toggleSort} right />
                <Th col="Difference"   label="Diff"         sort={sort} toggle={toggleSort} right />
                <Th col="ItemGroup"    label="Group"        sort={sort} toggle={toggleSort} />
                <Th col="Make"         label="Make"         sort={sort} toggle={toggleSort} />
                <th className="serial-col-hdr" style={{ minWidth:220 }}>In-stock Serials</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const diff = r.Difference ?? 0;
                return (
                  <tr key={i}>
                    <td>{r.ItemName}</td>
                    <td className="dim">{r.GodownName}</td>
                    <td><MismatchBadge type={r.MismatchType} /></td>
                    <td className="num">{r.StockQty}</td>
                    <td className="num">{r.SerialQty}</td>
                    <td className="num" style={{ color: diff > 0 ? '#e53e3e' : '#b7791f', fontWeight:600 }}>
                      {diff > 0 ? `+${diff}` : diff}
                    </td>
                    <td className="dim" style={{ fontSize:12 }}>{r.ItemGroup || '—'}</td>
                    <td className="dim" style={{ fontSize:12 }}>{r.Make || '—'}</td>
                    <td className="wrap serial-cell"><SerialCell list={r.SerialList} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
